import { useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../hooks/useAuth'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import Footer from '../Layout/Footer'
// Note: CompleteRegistration tracking moved to AuthContext.jsx (triggers after DOI/email confirmation)

function RegisterPage() {
  const { t } = useTranslation(['auth', 'common'])
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    // NEW: Email preferences
    acceptTerms: false,
    subscribeProductUpdates: false,
    subscribeMarketing: false
  })
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [registrationSuccess, setRegistrationSuccess] = useState(false)
  const [facebookLoading, setFacebookLoading] = useState(false)
  // Resend email states (grouped with other states)
  const [resendLoading, setResendLoading] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [resendError, setResendError] = useState('')
  const resendTimeoutRef = useRef(null)

  const { register, loginWithFacebook } = useAuth()
  const navigate = useNavigate()

  // Cleanup timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (resendTimeoutRef.current) {
        clearTimeout(resendTimeoutRef.current)
      }
    }
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    // NEW: Validate terms acceptance
    if (!formData.acceptTerms) {
      setError(t('validation.termsRequired'))
      return
    }

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError(t('validation.passwordMismatch'))
      return
    }

    if (formData.password.length < 8) {
      setError(t('validation.passwordTooShort'))
      return
    }

    setIsLoading(true)

    try {
      const result = await register(
        formData.email,
        formData.password,
        {
          display_name: formData.displayName,
          // NEW: Email preferences metadata
          email_preferences: {
            product_updates: formData.subscribeProductUpdates,
            marketing: formData.subscribeMarketing
          }
        }
      )

      // Note: CompleteRegistration tracking happens in AuthContext.jsx after email confirmation (DOI)
      // This ensures we only track verified users, not spam/fake registrations

      if (result.needsEmailConfirmation) {
        setRegistrationSuccess(true)
      } else {
        // If email confirmation is disabled, navigate to dashboard
        navigate('/dashboard')
      }
    } catch (err) {
      // Check for specific error keys from AuthContext
      if (err.message === 'email_rate_limit') {
        setError(t('errors:auth.emailRateLimit'))
      } else if (err.message === 'email_already_registered') {
        setError(t('errors:auth.emailAlreadyRegistered'))
      } else {
        setError(err.message || t('errors:auth.registrationFailed'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    })
  }

  const handleFacebookRegister = async () => {
    // Validate terms acceptance first
    if (!formData.acceptTerms) {
      setError(t('validation.termsRequired'))
      return
    }

    setError('')
    setFacebookLoading(true)
    try {
      await loginWithFacebook()
      // Redirect happens automatically via Supabase
    } catch (err) {
      setError(err.message || t('errors:auth.facebookFailed'))
      setFacebookLoading(false)
    }
  }

  // Resend verification email handler
  const handleResendEmail = useCallback(async () => {
    if (resendLoading || resendSuccess) return

    setResendLoading(true)
    setResendError('')

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: formData.email,
      })

      if (error) {
        // Handle Supabase rate limit (60 seconds between requests)
        if (error.message?.toLowerCase().includes('rate') || error.status === 429) {
          throw new Error(t('register.success.rateLimitError'))
        }
        throw error
      }

      setResendSuccess(true)
      // Reset success state after 30 seconds to allow another resend
      // Use ref to enable cleanup on unmount
      resendTimeoutRef.current = setTimeout(() => setResendSuccess(false), 30000)
    } catch (err) {
      // Always show translated error to avoid exposing Supabase internals
      setResendError(t('register.success.resendError'))
      console.error('Resend email failed:', err.message)
    } finally {
      setResendLoading(false)
    }
  }, [formData.email, resendLoading, resendSuccess, t])

  if (registrationSuccess) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-purple-600 to-pink-500">
        <div className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="max-w-md w-full">
            <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 md:p-8">
              <div className="text-center">
                {/* Email Icon instead of Checkmark */}
                <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                  <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>

                <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('register.success.title')}</h1>
                <p className="text-gray-600 mb-6">
                  {t('register.success.message')} <strong className="text-gray-900">{formData.email}</strong>
                </p>

                {/* Steps */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                  <p className="text-sm font-medium text-gray-700 mb-3">{t('register.success.stepsTitle')}</p>
                  <ol className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <span className="flex-shrink-0 w-5 h-5 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-xs font-medium mr-2">1</span>
                      {t('register.success.step1')}
                    </li>
                    <li className="flex items-start">
                      <span className="flex-shrink-0 w-5 h-5 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-xs font-medium mr-2">2</span>
                      {t('register.success.step2')}
                    </li>
                    <li className="flex items-start">
                      <span className="flex-shrink-0 w-5 h-5 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-xs font-medium mr-2">3</span>
                      {t('register.success.step3')}
                    </li>
                  </ol>
                </div>

                {/* Divider */}
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-sm text-gray-500 mb-3">{t('register.success.noEmail')}</p>

                  {/* Resend Button */}
                  <button
                    onClick={handleResendEmail}
                    disabled={resendLoading || resendSuccess}
                    aria-label={t('register.success.resendButton')}
                    className="text-sm font-medium text-purple-600 hover:text-purple-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    {resendLoading ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('register.success.resending')}
                      </span>
                    ) : resendSuccess ? (
                      <span className="flex items-center justify-center text-green-600">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {t('register.success.resent')}
                      </span>
                    ) : (
                      t('register.success.resendButton')
                    )}
                  </button>

                  {/* Resend Error */}
                  {resendError && (
                    <p role="alert" className="mt-2 text-sm text-red-600">{resendError}</p>
                  )}

                  {/* Spam Hint */}
                  <p className="mt-3 text-xs text-gray-400">{t('register.success.spamHint')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-purple-600 to-pink-500">
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 md:p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-500 rounded-xl mb-4">
              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1112.324 0 6.162 6.162 0 01-12.324 0zM12 16a4 4 0 110-8 4 4 0 010 8zm4.965-10.405a1.44 1.44 0 112.881.001 1.44 1.44 0 01-2.881-.001z"/>
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">{t('register.title')}</h1>
            <p className="mt-2 text-sm text-gray-600">
              {t('register.subtitle')}
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Register Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="displayName" className="label">
                {t('register.form.displayName.label')}
              </label>
              <input
                id="displayName"
                name="displayName"
                type="text"
                className="input"
                placeholder={t('register.form.displayName.placeholder')}
                value={formData.displayName}
                onChange={handleChange}
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="email" className="label">
                {t('register.form.email.label')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="input"
                placeholder={t('register.form.email.placeholder')}
                value={formData.email}
                onChange={handleChange}
                disabled={isLoading}
              />
            </div>

            <div>
              <label htmlFor="password" className="label">
                {t('register.form.password.label')}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                className="input"
                placeholder={t('register.form.password.placeholder')}
                value={formData.password}
                onChange={handleChange}
                disabled={isLoading}
              />
              <p className="mt-1 text-xs text-gray-500">
                {t('register.form.password.help')}
              </p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="label">
                {t('register.form.confirmPassword.label')}
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                className="input"
                placeholder={t('register.form.confirmPassword.placeholder')}
                value={formData.confirmPassword}
                onChange={handleChange}
                disabled={isLoading}
              />
            </div>

            {/* Email Preferences Section */}
            <div className="space-y-3 border-t border-gray-200 pt-4 mt-6">
              {/* Terms & Privacy - REQUIRED */}
              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  name="acceptTerms"
                  checked={formData.acceptTerms}
                  onChange={handleChange}
                  disabled={isLoading}
                  required
                  className="mt-1 mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">
                  {t('register.form.acceptTerms.text')}{' '}
                  <Link
                    to="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 underline hover:text-primary-700"
                  >
                    {t('register.form.acceptTerms.termsLink')}
                  </Link>
                  {' '}{t('register.form.acceptTerms.and')}{' '}
                  <Link
                    to="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 underline hover:text-primary-700"
                  >
                    {t('register.form.acceptTerms.privacyLink')}
                  </Link>
                </span>
              </label>

              {/* Product Updates - OPTIONAL */}
              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  name="subscribeProductUpdates"
                  checked={formData.subscribeProductUpdates}
                  onChange={handleChange}
                  disabled={isLoading}
                  className="mt-1 mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-600">
                  {t('register.form.subscribeProductUpdates')}
                </span>
              </label>

              {/* Marketing - OPTIONAL */}
              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  name="subscribeMarketing"
                  checked={formData.subscribeMarketing}
                  onChange={handleChange}
                  disabled={isLoading}
                  className="mt-1 mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-600">
                  {t('register.form.subscribeMarketing')}
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary py-3 flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('register.button.submitting')}
                </>
              ) : (
                t('register.button.submit')
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">{t('register.divider')}</span>
            </div>
          </div>

          {/* Facebook Register */}
          <button
            type="button"
            onClick={handleFacebookRegister}
            disabled={isLoading || facebookLoading || !formData.acceptTerms}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" fill="#1877F2" viewBox="0 0 24 24">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            {facebookLoading ? t('register.button.submitting') : t('register.facebook.button')}
          </button>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              {t('register.link.hasAccount')}{' '}
              <Link to="/login" className="text-primary-600 hover:text-primary-500 font-medium">
                {t('register.link.login')}
              </Link>
            </p>
          </div>
        </div>
        </div>
      </div>
      <Footer />
    </div>
  )
}

export default RegisterPage
